import React, { useEffect } from 'react';
import { useStore } from '../../../../../stores/root';

// assets
import { faCheck, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ValidationService from '../../../../../services/validation';
import OrderContainerService from '../../../../../services/orders/orderContainer';
import OrderService from '../../../../../services/orders/order';
import GeneralService from '../../../../../services/general';
import ErrorService from '../../../../../services/general/error';

// props

// components
import { Checkbox, DefaultButton, Dropdown, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import { PPNPercentageOptions, PPhPercentageOptions } from '../../../../../manifests/taxPercentage';
import Label from '../../../../typography/label';
import Text from '../../../../typography/text';
import PricingService from '../../../../../services/general/pricing';
import { IOrderContainerResourceCompleteProps } from '../../../../../props/orders/containers';
import Details from '../../../../typography/details';
import PermissionsService from '../../../../../services/permissions';

interface IOrderContainerFormProps {
    orderId: string;
    containerId?: string;
    onDismissed(refresh?: boolean): void;
}

type FormDataProps = {
    containerNumber: string;
    size: string;
    sealNumber: string;
    roNumber: string;
    voy: string;
    shipName: string;
    price: string;
    includePPn?: boolean;
    includePPh?: boolean;
    ppn: string;
    ppnPercentage: string;
    pph: string;
    pphPercentage: string;
    finalPrice: string;
    nettPrice: string;
    invoicePrice: string;
    stuffingType?: 'luar' | 'dalam';
    strippingType?: 'luar' | 'dalam';
}

type FormDataErrorProps = {
    containerNumber?: string;
    size?: string;
    sealNumber?: string;
    roNumber?: string;
    voy?: string;
    shipName?: string;
    price?: string;
    ppn?: string;
    pph?: string;
}

const OrderContainerForm: React.FC<IOrderContainerFormProps> = (props: IOrderContainerFormProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [container, setContainer] = React.useState<IOrderContainerResourceCompleteProps | undefined>();
    const [updatePrice, setUpdatePrice] = React.useState<boolean>(false);
    const [data, setData] = React.useState<FormDataProps>({
        containerNumber: "",
        size: "",
        sealNumber: "",
        roNumber: "",
        voy: "",
        shipName: "",
        price: "",
        ppn: "0",
        pph: "0",
        ppnPercentage: "0",
        pphPercentage: "0",
        finalPrice: "0",
        nettPrice: "0",
        invoicePrice: "0",
        stuffingType: "luar",
        strippingType: "luar"
    });
    const [error, setError] = React.useState<FormDataErrorProps>({});
    const mode: 'create' | 'update' = props.containerId === undefined ? 'create' : 'update';

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        let defaultShipName = "";
        let defaultVoy = "";

        try {
            if (props.orderId) {
                const orderData = await OrderService.get(props.orderId);
                if (orderData) {
                    defaultShipName = orderData.shipName || "";
                    defaultVoy = orderData.voy || "";
                }
            }
        } catch (e) {
            console.error("Failed to load order for container defaults", e);
        }

        if (props.containerId) {
            const _container = await OrderContainerService.get(props.orderId, props.containerId);

            setData({
                containerNumber: _container.containerNumber || "",
                size: _container.size || "",
                sealNumber: _container.sealNumber || "",
                roNumber: _container.roNumber || "",
                voy: _container.voy || defaultVoy || "",
                shipName: _container.shipName || defaultShipName || "",
                price: _container.price || "",
                ppn: Number(_container.ppn) + "",
                pph: Number(_container.pph) + "",
                ppnPercentage: Number(_container.ppnPercentage) + "",
                pphPercentage: Number(_container.pphPercentage) + "",
                finalPrice: _container.totalPrice + "",
                nettPrice: _container.nettPrice + "",
                invoicePrice: _container.invoicePrice + "",
                includePPn: _container.includePPn,
                includePPh: _container.includePPh,
                stuffingType: _container.stuffingType || 'luar',
                strippingType: _container.strippingType || 'luar'
            });
            setContainer(_container);
        } else {
            setData(prev => ({
                ...prev,
                shipName: defaultShipName,
                voy: defaultVoy
            }));
        }

        setLoaded(true);
    }

    const isSubmitButtonDisabled = (): boolean => {
        const hasErrors = Boolean(
            (error.containerNumber && error.containerNumber.trim() !== '') ||
            (error.size && error.size.trim() !== '') ||
            (error.sealNumber && error.sealNumber.trim() !== '') ||
            (error.roNumber && error.roNumber.trim() !== '') ||
            (error.voy && error.voy.trim() !== '') ||
            (error.shipName && error.shipName.trim() !== '')
        );

        if (hasErrors) {
            return true;
        }

        if (!data.containerNumber || data.containerNumber.trim() === '') {
            return true;
        }

        return false;
    }

    const _onSubmit = async () => {
        try {
            setSubmitting(true);
            const { containerNumber, price, roNumber, sealNumber, size, voy, shipName, ppn, pph, includePPn, includePPh, ppnPercentage, pphPercentage, finalPrice, nettPrice, invoicePrice } = data;

            // create form data
            let fd = new FormData();
            fd.append("containerNumber", containerNumber);
            fd.append("roNumber", roNumber);
            fd.append("size", size);
            fd.append("sealNumber", sealNumber);
            fd.append("voy", voy);
            fd.append("shipName", shipName);
            fd.append("stuffingType", data.stuffingType || "luar");
            fd.append("strippingType", data.strippingType || "luar");

            if (updatePrice) {
                fd.append("totalPrice", finalPrice);
                fd.append("finalPrice", finalPrice);
                fd.append("nettPrice", nettPrice);
                fd.append("invoicePrice", invoicePrice);
                fd.append("price", price);
                fd.append("ppn", ppn);
                fd.append("pph", pph);
                fd.append("includePPn", includePPn + "");
                fd.append("includePPh", includePPh + "");
                fd.append("ppnPercentage", ppnPercentage + "");
                fd.append("pphPercentage", pphPercentage + "");

                if (container?.invoiceId) {
                    fd.append("updateInvoice", "1");
                }
            } else {
                fd.append("ppnPercentage", ppnPercentage || "0");
                fd.append("pphPercentage", pphPercentage || "0");
            }

            if (props.containerId === undefined) {
                await OrderContainerService.new(props.orderId, fd);
            } else {
                await OrderContainerService.update(props.orderId, props.containerId, fd);
            }

            banner.add({
                key: mode + '_order_container_success',
                variant: 'success',
                icon: faCheck,
                text: `Container "${containerNumber}" ${mode === 'create' ? 'created' : 'updated'} successfully`
            });
            props.onDismissed(true);
        } catch (e) {
            setSubmitting(false);
            banner.add({
                key: mode + '_order_container_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: `Failed to ${mode} container. Error: ` + ErrorService.getMessage(e),
            });
        }
    }

    const calculateAllPrice = (_data: FormDataProps) => {
        const pricing = PricingService.order.calculate(_data, 'lumpsum');
        setData({ ..._data, ...pricing });
    }

    return <Panel headerText={mode === 'create' ? 'Create Container' : 'Update Container'}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {
                    !submitting ? (
                        <>
                            <PrimaryButton text={"Submit"} disabled={isSubmitButtonDisabled()} onClick={_onSubmit} />
                            <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                        </>
                    ) : null
                }
                {submitting ? <Spinner size={SpinnerSize.medium} labelPosition={"right"} label={mode === 'create' ? "Creating container ..." : "Updating container ..."} /> : null}
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 20 }}>
            {!loaded ? <Stack horizontalAlign={"baseline"}><Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Preparing form ..."} /></Stack> : null}
            {
                loaded ? <>
                    <Stack horizontal tokens={{ childrenGap: 20 }}>
                        <Stack.Item styles={{ root: { width: '50%' } }}>
                            <TextField label={"Container Number"}
                                required={true}
                                value={data.containerNumber}
                                onChange={(evt, value) => {
                                    const val = value || "";
                                    const validation = ValidationService.combination(val, ['required', 'limit'], { maxChars: 100 });
                                    setData(prev => ({ ...prev, containerNumber: val }));
                                    setError(prev => ({ ...prev, containerNumber: validation.message }));
                                }}
                                errorMessage={error.containerNumber}
                                disabled={submitting} />
                        </Stack.Item>
                        <Stack.Item styles={{ root: { width: '50%' } }}>
                            <Dropdown label={"Size"}
                                selectedKey={data.size}
                                options={[
                                    { key: '20FT', text: '20FT' },
                                    { key: '40FT', text: '40FT' },
                                    { key: '20FT HC', text: '20FT HC' },
                                    { key: '40FT HC', text: '40FT HC' }
                                ]}
                                onChange={(evt, opt) => {
                                    const val = opt ? opt.key as string : "";
                                    setData(prev => ({ ...prev, size: val }));
                                }} />
                        </Stack.Item>
                    </Stack>
                    <Stack horizontal tokens={{ childrenGap: 20 }}>
                        <Stack.Item styles={{ root: { width: '50%' } }}>
                            <TextField label={"Seal Number"}
                                value={data.sealNumber}
                                onChange={(evt, value) => {
                                    const val = value || "";
                                    const validation = ValidationService.combination(val, ['limit'], { maxChars: 100 });
                                    setData(prev => ({ ...prev, sealNumber: val }));
                                    setError(prev => ({ ...prev, sealNumber: validation.message }));
                                }}
                                errorMessage={error.sealNumber}
                                disabled={submitting} />
                        </Stack.Item>
                        <Stack.Item styles={{ root: { width: '50%' } }}>
                            <TextField label={"RO Number"}
                                value={data.roNumber}
                                onChange={(evt, value) => {
                                    const val = value || "";
                                    const validation = ValidationService.combination(val, ['limit'], { maxChars: 100 });
                                    setData(prev => ({ ...prev, roNumber: val }));
                                    setError(prev => ({ ...prev, roNumber: validation.message }));
                                }}
                                errorMessage={error.roNumber}
                                disabled={submitting} />
                        </Stack.Item>
                    </Stack>
                    <Stack horizontal tokens={{ childrenGap: 20 }}>
                        <Stack.Item styles={{ root: { width: '50%' } }}>
                            <TextField label={"Nama Kapal"}
                                value={data.shipName}
                                onChange={(evt, value) => {
                                    const val = value || "";
                                    const validation = ValidationService.combination(val, ['limit'], { maxChars: 100 });
                                    setData(prev => ({ ...prev, shipName: val }));
                                    setError(prev => ({ ...prev, shipName: validation.message }));
                                }}
                                errorMessage={error.shipName}
                                disabled={submitting} />
                        </Stack.Item>
                        <Stack.Item styles={{ root: { width: '50%' } }}>
                            <TextField label={"Voy"}
                                value={data.voy}
                                onChange={(evt, value) => {
                                    const val = value || "";
                                    const validation = ValidationService.combination(val, ['limit'], { maxChars: 100 });
                                    setData(prev => ({ ...prev, voy: val }));
                                    setError(prev => ({ ...prev, voy: validation.message }));
                                }}
                                errorMessage={error.voy}
                                disabled={submitting} />
                        </Stack.Item>
                    </Stack>
                    <Stack horizontal tokens={{ childrenGap: 20 }}>
                        <Stack.Item styles={{ root: { width: '50%' } }}>
                            <Dropdown label={"Tipe Stuffing"}
                                selectedKey={data.stuffingType || 'luar'}
                                options={[
                                    { key: 'luar', text: 'Stuffing Luar (Door/Gudang)' },
                                    { key: 'dalam', text: 'Stuffing Dalam (Depo Pelabuhan)' }
                                ]}
                                onChange={(evt, opt) => {
                                    if (opt) {
                                        setData(prev => ({ ...prev, stuffingType: opt.key as 'luar' | 'dalam' }));
                                    }
                                }}
                                disabled={submitting} />
                        </Stack.Item>
                        <Stack.Item styles={{ root: { width: '50%' } }}>
                            <Dropdown label={"Tipe Stripping"}
                                selectedKey={data.strippingType || 'luar'}
                                options={[
                                    { key: 'luar', text: 'Stripping Luar (Door/Gudang)' },
                                    { key: 'dalam', text: 'Stripping Dalam (Depo Pelabuhan)' }
                                ]}
                                onChange={(evt, opt) => {
                                    if (opt) {
                                        setData(prev => ({ ...prev, strippingType: opt.key as 'luar' | 'dalam' }));
                                    }
                                }}
                                disabled={submitting} />
                        </Stack.Item>
                    </Stack>
                    <Stack className={'divider'}></Stack>
                    {/*<Checkbox label={'Update price'}
                        checked={updatePrice}
                        disabled={(container?.invoiceId || "").trim() !== "" && !PermissionsService.hasPermission(['incomes.invoices.update.all'], user.permissions)}
                        onChange={(ev, checked) => setUpdatePrice(checked || false)} />
                    {container?.invoiceId && updatePrice ? <Text className={'color-yellow'}>!!! WARNING !!! This container already associated with an invoice, updating this container's price will also update invoice amount.</Text> : null}
                    <Stack.Item>
                        <TextField label={"Price"}
                            value={data.price}
                            onChange={(evt, value) => {
                                if (!isNaN(Number(value))) {
                                    data.price = value || "";

                                    const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                    error.price = validation.message;

                                    setError({ ...error });

                                    calculateAllPrice(data);
                                }
                            }}
                            prefix={"Rp"}
                            description={data.price !== "" ? GeneralService.convertNumberToWords(Number(data.price)) : undefined}
                            errorMessage={error.price}
                            disabled={submitting || !updatePrice}
                            required={true} />
                    </Stack.Item>
                    <Stack tokens={{ childrenGap: 20 }} horizontal>
                        <Stack.Item>
                            <Checkbox checked={data.includePPn}
                                label='Include PPn'
                                disabled={submitting || !updatePrice}
                                onChange={(ev, checked) => {
                                    data.includePPn = checked;
                                    setData({ ...data });
                                    calculateAllPrice(data);
                                }} />
                        </Stack.Item>
                        <Stack.Item>
                            <Checkbox checked={data.includePPh}
                                disabled={submitting || !updatePrice}
                                label='Include PPh'
                                onChange={(ev, checked) => {
                                    data.includePPh = checked;
                                    setData({ ...data });
                                    calculateAllPrice(data);
                                }} />
                        </Stack.Item>
                    </Stack>
                    <Stack tokens={{ childrenGap: 2 }}>
                        <Label size={'small'}>PPn</Label>
                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                            <Stack.Item>
                                <Dropdown options={PPNPercentageOptions}
                                    selectedKey={data.ppnPercentage}
                                    onChange={(evt, opt) => {
                                        if (opt && opt.key !== 'custom') {
                                            data.ppn = (Number(opt.key) / 100 * Number(data.price)).toFixed();
                                        }
                                        data.ppnPercentage = (opt?.key || "") as string;

                                        setData({ ...data });
                                        calculateAllPrice(data);
                                    }}
                                    disabled={submitting || !updatePrice}
                                    styles={{ root: { width: 100 } }} />
                            </Stack.Item>
                            <Stack.Item grow={1}>
                                <TextField required={true}
                                    value={data.ppn}
                                    onChange={(evt, value) => {
                                        if (!isNaN(Number(value))) {
                                            data.ppn = value || "";

                                            const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                            error.ppn = validation.message;

                                            setData({ ...data });
                                            setError({ ...error });
                                            calculateAllPrice(data);
                                        }
                                    }}
                                    prefix={"Rp"}
                                    errorMessage={error.ppn}
                                    disabled={submitting || data.ppnPercentage !== 'custom' || !updatePrice} />
                            </Stack.Item>
                        </Stack>
                    </Stack>
                    <Stack tokens={{ childrenGap: 2 }}>
                        <Label size={'small'}>PPh</Label>
                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                            <Stack.Item>
                                <Dropdown options={PPhPercentageOptions}
                                    selectedKey={data.pphPercentage}
                                    onChange={(evt, opt) => {
                                        if (opt && opt.key !== 'custom') {
                                            data.pph = (Number(opt.key) / 100 * Number(data.price)).toFixed();
                                        }
                                        data.pphPercentage = (opt?.key || "") as string;

                                        setData({ ...data });
                                        calculateAllPrice(data);
                                    }}
                                    disabled={submitting || !updatePrice}
                                    styles={{ root: { width: 100 } }} />
                            </Stack.Item>
                            <Stack.Item grow={1}>
                                <TextField required={true}
                                    value={data.pph}
                                    onChange={(evt, value) => {
                                        if (!isNaN(Number(value))) {
                                            data.pph = value || "";

                                            const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                            error.pph = validation.message;

                                            setData({ ...data });
                                            setError({ ...error });
                                            calculateAllPrice(data);
                                        }
                                    }}
                                    prefix={"Rp"}
                                    errorMessage={error.pph}
                                    disabled={submitting || data.pphPercentage !== 'custom' || !updatePrice} />
                            </Stack.Item>
                        </Stack>
                    </Stack>
                    <Stack className={'divider'}></Stack>
                    <Stack horizontal tokens={{childrenGap: 20}}>
                        <Details title={'Total price'} text={`Rp. ${GeneralService.getNumberWithSeparator(data.finalPrice)}`} style={{width: '50%'}} />
                        <Details title={'Invoice price'} text={`Rp. ${GeneralService.getNumberWithSeparator(data.invoicePrice)}`} style={{width: '50%'}} />
                    </Stack>*/}
                </> : null
            }
        </Stack>
    </Panel>
};

export default OrderContainerForm;
